import { capitalize, filter, isEmpty } from 'lodash'
import PropTypes from 'prop-types' // eslint-disable-line no-unused-vars
import Breadcrumbs from 'shared/components/breadcrumbs/breadcrumbs'
import ChannelAnalyticsOverview from 'shared/components/channel-analytics/channel-analytics'
import { UsernameBadge } from 'shared/components/users'
import resizeAvatar from 'shared/util/resize-avatar'
import { removeWords } from 'shared/util/string'
import { parse } from 'url'
import channelProps from '../../prop-types/channel'
import ChannelListLiveHeader from '../channel-live-logo/live-header'
import css, { avatarSize, avatarSmallSize } from './info.css'
import NoInfo from './no-info'
import Search from './search'
import Social from './social'
import { StickerEmbedButton } from './sticker-embed-modal'
import Tags from './tags'

const IMAGE_TYPES = ['gif', 'gifs', 'sticker', 'stickers']
const AVATAR_SIZE = parseInt(avatarSize)
const AVATAR_SMALL_SIZE = parseInt(avatarSmallSize)

const Actions = ({ channel: { id, content_type } }) => (
    <div className={css.actions}>{content_type === 'sticker' ? <StickerEmbedButton channelId={id} /> : null}</div>
)

const getSocialLinks = ({ facebook_url, twitter_url, instagram_url, tumblr_url }) => {
    const social = [
        { type: 'facebook', url: facebook_url },
        { type: 'twitter', url: twitter_url },
        { type: 'instagram', url: instagram_url },
        { type: 'tumblr', url: tumblr_url },
    ]

    // remove any items with no url
    return filter(social, item => item.url)
}

const MobileBreadcrumbs = ({
    ancestors,
    ancestors: [ancestor],
    mobileTitle,
    shortDisplayName,
    search,
    primarySite,
    slug,
}) =>
    ancestor ? ( // branch & leaf pages
        <Breadcrumbs
            breadcrumbs={ancestors}
            displayName={mobileTitle}
            shortDisplayName={shortDisplayName}
            casing={`capitalize`}
        />
    ) : search || primarySite ? ( // search or root channel
        <a
            className={css.mobileWebsite}
            style={{ textTransform: `none` }}
            href={search ? `/${slug}` : primarySite}
            rel="noopener noreferrer"
            target="_blank"
        >
            {search ? mobileTitle : parse(primarySite).hostname}
        </a>
    ) : null

const LiveMobileTitle = ({ displayName }) => (
    <div className={css.liveMobileTitle}>
        <ChannelListLiveHeader isMobile={true} />
        <h1 className={css.titleSmall}>{removeWords(displayName, IMAGE_TYPES)}</h1>
    </div>
)

const Description = ({ isOwner, user, socialLinks, ancestors, isCompact, mobileTitle, shortDisplayName, search }) => {
    const { primary_site: primarySite, about_bio: aboutBio, permissions = {}, slug, id } = user
    const showDashboardLink = isOwner && permissions.dashboard_link

    if (isOwner && !isCompact && !primarySite && !aboutBio && isEmpty(socialLinks)) {
        return <NoInfo />
    }

    return (
        <div>
            <ChannelAnalyticsOverview
                showDashboardLink={showDashboardLink}
                permissions={permissions}
                isOwner={isOwner}
                userId={id}
                isCompact={isCompact}
            />
            {aboutBio ? <p className={isCompact ? css.bioSmall : css.bio}>{aboutBio}</p> : null}
            {isCompact && (
                <div className={css.breadcrumbs}>
                    <MobileBreadcrumbs
                        ancestors={ancestors}
                        mobileTitle={mobileTitle}
                        shortDisplayName={shortDisplayName}
                        slug={slug}
                        primarySite={primarySite}
                        search={search}
                    />
                </div>
            )}
            {primarySite && !isCompact ? (
                <a className={css.website} href={primarySite} target="_blank" rel="noopener noreferrer">
                    {parse(primarySite).hostname}
                </a>
            ) : null}
            {!isEmpty(socialLinks) ? <Social links={socialLinks} isCompact={isCompact} /> : null}
        </div>
    )
}

const Loader = ({ isCompact }) => (
    <div className={isCompact ? css.containerSmall : css.container}>
        <div className={isCompact ? css.headerSmall : ''}>
            <div className={isCompact ? css.avatarSmallBackgroundFade : css.avatarBackgroundFade} />
        </div>
    </div>
)

const Info = ({ isOwner, channel, search, hideDescription, isCompact, isTagView, isLoading, title, children }) => {
    if (isLoading) {
        return <Loader isCompact={isCompact} />
    } else {
        const {
            tags,
            user,
            is_live: isLive,
            slug,
            banner_image,
            ancestors,
            description,
            ancestors: [ancestor],
            display_name: channelDisplayName,
            short_display_name: shortDisplayName,
            content_type: contentType,
        } = channel
        const avatarUrl = ancestor ? ancestor.slug : slug
        const { avatar, username } = user
        const canSearch = user && user.user_type !== 'user'
        const mobileTitle = title || shortDisplayName || channelDisplayName || username
        const showBreadcrumbs = hideDescription && isCompact
        return (
            <div className={isCompact ? css.containerSmall : css.container}>
                <div className={isCompact ? css.headerSmall : ''} data-has-banner={!!banner_image}>
                    {avatar ? (
                        <a
                            href={`/${avatarUrl}`}
                            className={isCompact ? css.avatarSmall : css.avatar}
                            data-has-banner={!!banner_image}
                            style={{
                                backgroundImage: `url(${resizeAvatar(
                                    avatar,
                                    isCompact ? AVATAR_SMALL_SIZE : AVATAR_SIZE
                                )})`,
                            }}
                        />
                    ) : null}
                    {isCompact &&
                        (isLive ? (
                            <LiveMobileTitle displayName={mobileTitle} />
                        ) : (
                            <h1 className={css.titleSmall} data-has-banner={!!banner_image}>
                                {search
                                    ? `${removeWords(capitalize(search), IMAGE_TYPES)} ${
                                          contentType === 'sticker' ? 'Stickers' : 'GIFs'
                                      }`
                                    : mobileTitle}
                            </h1>
                        ))}
                    {isCompact && (
                        <div className={css.usernameSmall}>
                            <UsernameBadge user={user} />
                        </div>
                    )}
                </div>
                {showBreadcrumbs && (
                    <div>
                        <div className={css.breadcrumbs}>
                            <MobileBreadcrumbs
                                ancestors={ancestors}
                                mobileTitle={mobileTitle}
                                shortDisplayName={shortDisplayName}
                                slug={slug}
                                search={search}
                            />
                        </div>
                        {description ? <p className={isCompact ? css.bioSmall : css.bio}>{description}</p> : null}
                    </div>
                )}
                {!hideDescription ? (
                    <Description
                        isOwner={isOwner}
                        channelId={channel.id}
                        ancestors={channel.ancestors}
                        mobileTitle={mobileTitle}
                        user={user}
                        isCompact={isCompact}
                        socialLinks={getSocialLinks(user)}
                        shortDisplayName={shortDisplayName}
                        search={search}
                    />
                ) : null}
                {!isCompact && canSearch ? (
                    <Search channel={channel} query={search} isTagView={isTagView} contentType={contentType} />
                ) : null}
                {!isEmpty(tags) ? (
                    <Tags channel={channel} search={search} isCompact={isCompact} isTagView={isTagView} />
                ) : null}
                {!isCompact && <Actions channel={channel} />}
                {children}
            </div>
        )
    }
}

Info.propTypes = {
    channel: channelProps,
    search: PropTypes.string,
    title: PropTypes.string, // optionally override channel title stuff
    hideDescription: PropTypes.bool,
    isCompact: PropTypes.bool,
    isTagView: PropTypes.bool,
    isLoading: PropTypes.bool,
}

export default Info
